/**
 * useCurrentTime — returns a Date that ticks once a minute.
 *
 * Used to render the "now" indicator line in the calendar Week/Day
 * views.  The tick is intentionally minute-resolution (not seconds)
 * because the calendar grid's smallest cell is 15 minutes and
 * re-rendering every second would be wasteful.
 *
 * @param {Object}  [options]
 * @param {React.MutableRefObject<boolean>} [options.pauseRef]
 *        Optional ref pointing at a boolean.  While `pauseRef.current`
 *        is true, the hook skips its tick — used to avoid spurious
 *        re-renders while the user is mid-edit inside a dialog.
 * @returns {Date}
 */

import { useState, useEffect } from 'react';

const TICK_INTERVAL_MS = 60_000;

export const useCurrentTime = ({ pauseRef } = {}) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => {
      if (pauseRef?.current) return;
      setNow(new Date());
    }, TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pauseRef]);

  return now;
};

export default useCurrentTime;
